import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import { getAttributeGroupKeys, getAttributeGroupsForPath } from "csi/middleware/attributes/attributeKeysService";
import { ATTR_IMAGES } from "csi/model/image";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { C8AttributeKey } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ILocaleProvider } from "designSystem/locale";
import _ from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { ATTR_NAME, C8NodeType } from "src/c8-platform/type/node";
import { getCustomViewContainerName, getCustomViewDomainClassPaths, getCustomViewPathImageLabelKeys, getCustomViewPathKeys, getCustomViewPathMetaDataKeys } from "../customViewService";
import CustomViewAttribute from "./CustomViewAttribute";
import CustomViewAttributeGroup from "./CustomViewAttributeGroup";
import CustomViewAttributesLevel from "./CustomViewAttributesLevel";
import CustomViewAttributesStore from "./CustomViewAttributesStore";

const CALLOUT_IDS = {
	[ATTR_NAME]: 1,
	[ATTR_IMAGES]: 2,
	Active: 3,
	Code: 4,
};

class CustomViewAttributeContainer extends ReactionStore {
	@observable.ref private _groups: CustomViewAttributeGroup[];

	/** indicates if container is expanded */
	@observable expanded = false;

	/** reflection class this container was derived from */
	readonly derivedFrom: ReflectionClass;

	/** path to container from starting class */
	readonly extendedPath: ExtendedPath;

	/** indicates if this container is the source container */
	readonly isSource: boolean;

	/** attributes level to which container belongs */
	readonly level: CustomViewAttributesLevel;

	/** spec for matrix group container which only exists on source of matrix (i.e. Style->Colorways), not all containers within matrix (ColorSpecification) */
	readonly sourceMatrixGroupSpec?: Spec;

	/** return attribute ids */
	get attributeIds(): C8AttributeKey[] {
		return Object.keys(this.attributeMap);
	}

	/** return map of attributes by their key */
	@computed get attributeMap(): Record<C8AttributeKey, CustomViewAttribute> {
		const { groups } = this;
		const map = {};

		groups.forEach(({ attributes }) => {
			attributes.forEach((attribute) => {
				map[attribute.id] = attribute;
			});
		});

		return map;
	}

	/** return map of attributes by their key, not affected by filtering of the search bar */
	@computed get attributeMapForGroupingAttributes(): Record<C8AttributeKey, CustomViewAttribute> {
		const { _groups } = this;
		const map = {};

		_groups.forEach(({ attributes }) => {
			attributes.forEach((attribute) => {
				if (attribute.spec.canGroup()) {
					map[attribute.id] = attribute;
				}
			});
		});

		return map;
	}

	/** indicate if additional paths can be shown for container, which is only the case now for matrix groups */
	get canShowAdditionalPaths(): boolean {
		const { sourceMatrixGroupSpec, store } = this;
		return !store.selectedMatrixContainer && !!sourceMatrixGroupSpec && !sourceMatrixGroupSpec?.heading.isConfiguredMatrixGroup;
	}

	/** return true if container has no attributes to display */
	get filteredOut(): boolean {
		const { groups, store: { searchText }, _groups } = this;

		// if container not yet populated and not searching, then display
		if (!_groups && !searchText) {
			return false;
		}

		return !groups.length;
	}

	/** path of container relative to attributes level - for matrix levels, that means return the path relative to the matrix path */
	get extendedPathInLevel(): ExtendedPath {
		const { extendedPath, matrixGroupSpec } = this;
		return matrixGroupSpec ? extendedPath.relativeTo(matrixGroupSpec.matrixExtendedPath) : extendedPath;
	}

	/** list of attribute groups in container */
	@computed get groups(): CustomViewAttributeGroup[] {
		const {
			store: { searchRegex },
			_groups,
		} = this;

		if (!_groups || !searchRegex) {
			return _groups ?? [];
		}

		return _.compact(_groups.map((group) => group.createFilteredGroup(searchRegex)));
	}

	/** locale provider for container */
	get loc(): ILocaleProvider {
		return this.store.loc;
	}

	/** name for container */
	@computed get name(): string {
		const { extendedPathInLevel, loc, sourceMatrixGroupSpec } = this;
		return sourceMatrixGroupSpec?.name || getCustomViewContainerName(loc, extendedPathInLevel);
	}

	/** node type for container */
	get nodeType(): C8NodeType {
		return this.extendedPath.endType;
	}

	/** parent attributes store */
	get store(): CustomViewAttributesStore {
		return this.level.store;
	}

	/** matrix group id that container is a part of */
	get matrixGroupId(): string | undefined {
		return this.matrixGroupSpec?.heading.matrixGroupId;
	}

	/** matrix group spec that container is a part of */
	get matrixGroupSpec(): Spec | undefined {
		const container = this.level.sourceMatrixContainer || this;
		return container.sourceMatrixGroupSpec;
	}

	/** unique id for container */
	get uniqueId(): string {
		return Key.getPathAndMatrixId(this.extendedPath, this.matrixGroupId);
	}

	/** pass request to store to show additional paths for this matrix container */
	@action onClickAdditionalPaths = (): void => {
		const { showMatrixGroupContainers, setSearchText } = this.store;
		setSearchText("");
		showMatrixGroupContainers(this);
	};

	/** create attributes for container if not already done, and expand if desired */
	@action populate = (forceExpand?: boolean): void => {
		const { extendedPath: { isOmitted }, sourceMatrixGroupSpec, _groups } = this;

		if (forceExpand) {
			this.expanded = true;
		}

		if (_groups) {
			return;
		}

		if (sourceMatrixGroupSpec?.heading.isConfiguredMatrixGroup) {
			this.#populateAttributesFromMatrixGroup();
		} else if (isOmitted) {
			this.#populateAttributesFromOmittedPath();
		} else {
			this.#populateAttributesFromPath();
		}
	};

	/** reevaluate selections of each group in this container based on selection set */
	@action reevaluateSelections = (): void => {
		const { groups } = this;
		groups.forEach(({ attributes }) => {
			attributes.forEach((attribute) => {
				attribute.reevaluateSelection();
			});
		});
	};

	/** set selected to true for attribute within given group; if no group provided, then select attribute in all groups (if already in a group) */
	@action selectAttribute = (attribute: CustomViewAttribute, supportsGrouping?: boolean, selectGroupSpec?: Spec, selectAll?: boolean): void => {
		const { groups, selectAttributeInGroup } = this;
		const { id: attributeKey, parentGroup } = attribute;
		const { isAttributeGroup, spec: groupSpec } = parentGroup;

		attribute.selected = true;

		// if grouping is not supported in selections to the right, and attribute is in an attribute group (on left), then select it in all groups
		if (selectAll) {
			groups.forEach(({ attributes }) => {
				selectAttributeInGroup(attributes, attributeKey, true);
			});
		} else if (!supportsGrouping && isAttributeGroup) {
			groups.forEach(({ attributes, spec }) => {
				if ((spec && (groupSpec !== spec))) {
					selectAttributeInGroup(attributes, attributeKey, true);
				}
			});
		} else {
			groups.some(({ attributes, flat, spec }) => {
				if ((!selectGroupSpec && flat) || (selectGroupSpec === spec)) {
					selectAttributeInGroup(attributes, attributeKey, true);
					return true;
				}
			});
		}
	};

	/** Selects an attribute within a group by its key by setting its 'selected' property to true. */
	@action selectAttributeInGroup = (attributes: CustomViewAttribute[], attributeKey: string, select: boolean): void => {
		const attributeToSelect = attributes.find(({ id }) => id === attributeKey);
		if (attributeToSelect) {
			attributeToSelect.selected = select;
		}
	};

	/** expand or collapse the container */
	@action toggleExpanded = (): void => {
		this.expanded = !this.expanded;
		this.populate();
	};

	/** set selected to false for attribute(s) with given key; if groupSpec passed, then only remove the attribute within that group */
	@action unselectAttribute = (attribute: CustomViewAttribute, groupSpec?: Spec): void => {
		const { groups, store } = this;

		// the attribute non-existent within selections? if so, remove all
		const { existsInSelections, id } = attribute;

		// attribute groups don't occur in matrix groups, so if available attribute is in multiple attribute groups,
		// then unselect them all; same goes for views that do not support grouping
		if (!store.supportsGrouping || groupSpec?.isMatrix) {
			groupSpec = null;
		}

		groups.forEach(({ attributes, spec, flat }) => {
			if (!existsInSelections || (!groupSpec && flat) || (groupSpec === spec)) {
				this.selectAttributeInGroup(attributes, id, false);
			}
		});
	};

	/** create attributes for Headings that are explicitly configured in the view */
	#createConfiguredAttributesForPath = (extendedPath: ExtendedPath, includeEmptyPaths: boolean, usedHeadingKeys?: Set<string>): CustomViewAttribute[] => {
		const { level, matrixGroupId, store: { createAttribute } } = this;
		const specs = level.specBuilder.getConfiguredSpecsForPath(extendedPath, matrixGroupId, includeEmptyPaths, usedHeadingKeys);
		return specs.map((spec) => createAttribute(this, spec));
	};

	/** create attributes that are explicitly configured in matrix group */
	#populateAttributesFromMatrixGroup(): void {
		const { sourceMatrixGroupSpec, store: { createAttribute, selectionSet }, store, uniqueId } = this;
		const attributes = sourceMatrixGroupSpec.subs.map((spec) => createAttribute(this, spec));
		attributes.forEach(action((attribute: CustomViewAttribute): void => {
			if (selectionSet.has(attribute.id)) {
				attribute.selected = true;
			}
		}));
		this._groups = [new CustomViewAttributeGroup(store, uniqueId, attributes)];
	}

	/** create attributes for container from configured Headings in view along an omitted path */
	#populateAttributesFromOmittedPath(): void {
		const { extendedPath, isSource, store, uniqueId } = this;
		const domainPaths = getCustomViewDomainClassPaths(extendedPath);
		const domainAttributes = domainPaths.flatMap((ep, i) => this.#createConfiguredAttributesForPath(ep, isSource && !!i));
		const sorted = _.sortBy(domainAttributes, (a) => a.spec.name);
		this._groups = [new CustomViewAttributeGroup(store, uniqueId, sorted)];
	}

	/** standard case where we create all attributes for container from class, as well as any other configured Headings in view */
	#populateAttributesFromPath(): void {
		const {
			derivedFrom,
			extendedPath,
			isSource,
			level,
			loc,
			matrixGroupId,
			store,
		} = this;

		const {
			value: uniqueId,
		} = extendedPath;

		const {
			createAttributeSpec,
			specBuilder,
		} = level;

		const {
			canShowAttribute,
			createAttribute,
			options,
		} = store;
		
		const {
			allowHidden,
			omitImages,
			omitMetadata,
			omitReferencedImages,
			optionalAttributesMaps,
		} = options;

		const usedHeadingKeys = new Set<string>();
		const groups: CustomViewAttributeGroup[] = [];

		const addGroup = (id: string, name: string, attributes: CustomViewAttribute[], spec?: Spec, first = false): void => {
			let group = groups.find((g) => g.name === name);

			if (group) {
				// merge groups with same name (attribute groups should now be uniquely named, but migrated customers may have duplicates)
				attributes.forEach((attribute) => {
					group.addAttribute(attribute);
				});
			} else {
				// create new group
				group = new CustomViewAttributeGroup(store, id, attributes, name, false, spec);

				if (first) {
					groups.unshift(group);
				} else {
					groups.push(group);
				}
			}
		};

		const keyToAttribute = (key: Key): CustomViewAttribute => {
			return createAttribute(this, createAttributeSpec(key, null, usedHeadingKeys, true));
		};

		// get and group all attributes in domain class
		const allowImageKeys = !omitReferencedImages;
		const attributeGroups = derivedFrom ? [] : getAttributeGroupsForPath(extendedPath);
		const keys = getCustomViewPathKeys(extendedPath, matrixGroupId, allowHidden, allowImageKeys, omitImages, optionalAttributesMaps, derivedFrom).filter((key) => canShowAttribute(key));
		const imageKeys = omitImages ? [] : getCustomViewPathImageLabelKeys(extendedPath, matrixGroupId);
		const metaDataKeys = (derivedFrom || omitMetadata) ? [] : getCustomViewPathMetaDataKeys(extendedPath, matrixGroupId);
		const keyMap = _.keyBy(keys, (key) => key.toString());

		attributeGroups.forEach(([group, groupPath]) => {
			const { name } = group;
			const keys = getAttributeGroupKeys(group, groupPath, matrixGroupId, allowImageKeys, omitImages, optionalAttributesMaps);

			if (keys.length) {
				const groupKey = Key.getAttributeGroupKey(name, groupPath, matrixGroupId);
				const spec = createAttributeSpec(groupKey, { name }, null, true);

				// map keys to attributes and remove any grouped keys from main list
				const attributes = keys.map((key) => {
					delete keyMap[key.toString()];
					return keyToAttribute(key);
				});

				addGroup(spec.uniqueId, name, attributes, spec);
			}
		});

		// image labels
		if (imageKeys.length) {
			addGroup(`___Images:${uniqueId}`, loc.resource("Images"), imageKeys.map(keyToAttribute));
		}

		// meta-data
		if (metaDataKeys.length) {
			addGroup(`___Metadata:${uniqueId}`, loc.resource("CustomViewAttributesMetadata"), metaDataKeys.map(keyToAttribute));
		}

		// convert remaining keys to attributes and sort by name, with some specific attributes coming first
		const remainingKeys = keys.filter((key) => !!keyMap[key.toString()]);
		const calloutKeys = remainingKeys.filter(({ id }) => CALLOUT_IDS[id]);
		let sortedCalloutKeys = _.sortBy(calloutKeys, ({ id }) => CALLOUT_IDS[id]);
		
		// remove callouts from remaining keys
		_.pullAll(remainingKeys, calloutKeys);
		
		// if Name Property configured in container, then filter out name attribute as well
		const namePropertyKey = specBuilder.getConfiguredNamePropertyForPath(extendedPath, matrixGroupId, usedHeadingKeys);
		if (namePropertyKey) {
			const originalSortedCalloutKeysLength = sortedCalloutKeys.length;
			sortedCalloutKeys = sortedCalloutKeys.filter(({ id }) => id !== ATTR_NAME);
			// if Node Name attribute was removed, then add Name Property as first attribute
			if (originalSortedCalloutKeysLength !== sortedCalloutKeys.length) {
				sortedCalloutKeys.unshift(namePropertyKey);
			}
		}

		// get attributes for callouts and remaining keys
		const callouts = sortedCalloutKeys.map(keyToAttribute);
		const remaining = remainingKeys.map(keyToAttribute);

		// get attributes for Headings configured in view that are not attributes
		const configured = this.#createConfiguredAttributesForPath(extendedPath, isSource, usedHeadingKeys);

		// combine them all together into main group
		const attributes = remaining.concat(configured);

		if (attributes.length || callouts.length) {
			const sorted = _.sortBy(attributes, (a) => a.spec.name);
			addGroup(uniqueId, "", [...callouts, ...sorted], null, true);
		}

		this._groups = groups;
	}

	constructor(
		level: CustomViewAttributesLevel,
		extendedPath: ExtendedPath,
		derivedFrom?: ReflectionClass,
		sourceMatrixGroupSpec?: Spec,
		isSource?: boolean,
	) {
		super();
		makeObservable(this);
		this.derivedFrom = derivedFrom;
		this.extendedPath = extendedPath;
		this.isSource = !!isSource;
		this.level = level;
		this.sourceMatrixGroupSpec = sourceMatrixGroupSpec;
	}
}

export default CustomViewAttributeContainer;